import { Autocomplete, FormControl, TextField } from '@mui/material';
import { startTransition, useCallback, useOptimistic } from 'react';
import { useSearchParams } from 'react-router';
import z from 'zod';

import { drawerZIndex } from '../../components/filter/FilterDrawer';
import { FilterSelectedLabel } from '../../components/filter/filterUtils';
import { trpc } from '../../trpc';

export const fehlerTypFilterKey = 'fehlerTyp';

type FehlerTypFilterProps = {
  readonly inDrawer?: boolean;
};

export function useFehlerTypFilter() {
  const [searchParams, setSearchParams] = useSearchParams();

  const actualValue = z.array(z.string()).parse(JSON.parse(searchParams.get(fehlerTypFilterKey) ?? '[]'));

  const [optimisticFehlerTypFilter, setOptimisticFehlerTypFilter] = useOptimistic(
    actualValue,
    (_, newValue: string[]) => newValue,
  );

  const setFehlerTypFilter = useCallback(
    (value: string[]) => {
      startTransition(() => {
        setOptimisticFehlerTypFilter(value);
        setSearchParams(
          // The prev parameter from react-router may be stale, see https://github.com/remix-run/react-router/issues/9757
          () => {
            const prev = new URLSearchParams(window.location.search);
            if (value.length === 0) {
              prev.delete(fehlerTypFilterKey);
            } else {
              prev.set(fehlerTypFilterKey, JSON.stringify(value));
            }
            return prev;
          },
          { replace: true },
        );
      });
    },
    [setSearchParams, setOptimisticFehlerTypFilter],
  );

  return [optimisticFehlerTypFilter, setFehlerTypFilter] as const;
}

export const FehlerTypFilter = ({ inDrawer }: FehlerTypFilterProps) => {
  const fehlerTypenQuery = trpc.fehler.getFehlerTypen.useQuery();
  const [fehlerTypFilter, setFehlerTypFilter] = useFehlerTypFilter();

  return (
    <FormControl sx={{ width: inDrawer ? '100%' : '12rem' }}>
      <Autocomplete
        id="fehler-typen-input"
        size="small"
        multiple
        value={fehlerTypFilter}
        onChange={(_, newValues) => {
          setFehlerTypFilter(newValues);
        }}
        options={fehlerTypenQuery.data ?? []}
        limitTags={0}
        slotProps={{ popper: { sx: { zIndex: (theme) => drawerZIndex(theme) + 1 } } }}
        getLimitTagsText={FilterSelectedLabel}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Fehler-Typ"
            sx={{
              '& input': {
                position: () => (fehlerTypFilter.length > 0 ? 'absolute' : undefined),
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};
